import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, Target, Check, X, RotateCcw, 
  Trophy, Sparkles, ArrowRight
} from 'lucide-react';

// Drag & Drop vocabulary sets - EXPANDED CONTENT
const MATCHING_SETS = [
  {
    id: 'basic',
    name: 'Temel Kelimeler',
    pairs: [
      { en: 'Aircraft', tr: 'Uçak' },
      { en: 'Cabin', tr: 'Kabin' },
      { en: 'Crew', tr: 'Mürettebat' },
      { en: 'Passenger', tr: 'Yolcu' },
      { en: 'Seat', tr: 'Koltuk' },
      { en: 'Aisle', tr: 'Koridor' },
      { en: 'Window', tr: 'Pencere' },
      { en: 'Cockpit', tr: 'Kokpit' },
      { en: 'Captain', tr: 'Kaptan' },
      { en: 'Flight Attendant', tr: 'Kabin Memuru' }
    ]
  },
  {
    id: 'safety',
    name: 'Güvenlik',
    pairs: [
      { en: 'Seatbelt', tr: 'Emniyet Kemeri' },
      { en: 'Life vest', tr: 'Can Yeleği' },
      { en: 'Emergency exit', tr: 'Acil Çıkış' },
      { en: 'Oxygen mask', tr: 'Oksijen Maskesi' },
      { en: 'Fire extinguisher', tr: 'Yangın Söndürücü' },
      { en: 'First aid kit', tr: 'İlk Yardım Çantası' },
      { en: 'Emergency slide', tr: 'Acil Kaydırak' },
      { en: 'Brace position', tr: 'Koruma Pozisyonu' },
      { en: 'Evacuation', tr: 'Tahliye' },
      { en: 'Safety card', tr: 'Güvenlik Kartı' }
    ]
  },
  {
    id: 'service',
    name: 'Servis',
    pairs: [
      { en: 'Beverage', tr: 'İçecek' },
      { en: 'Meal', tr: 'Yemek' },
      { en: 'Blanket', tr: 'Battaniye' },
      { en: 'Pillow', tr: 'Yastık' },
      { en: 'Headphones', tr: 'Kulaklık' },
      { en: 'Magazine', tr: 'Dergi' },
      { en: 'Hot towel', tr: 'Sıcak Havlu' },
      { en: 'Snack', tr: 'Atıştırmalık' },
      { en: 'Amenity kit', tr: 'Konfor Seti' },
      { en: 'Refreshment', tr: 'Serinletici' }
    ]
  },
  {
    id: 'announcements',
    name: 'Anonslar',
    pairs: [
      { en: 'Boarding', tr: 'Biniş' },
      { en: 'Departure', tr: 'Kalkış' },
      { en: 'Landing', tr: 'İniş' },
      { en: 'Turbulence', tr: 'Türbülans' },
      { en: 'Delay', tr: 'Rötar' },
      { en: 'Gate', tr: 'Kapı' },
      { en: 'Arrival', tr: 'Varış' },
      { en: 'Descent', tr: 'Alçalma' },
      { en: 'Cruising', tr: 'Seyir' },
      { en: 'Taxiing', tr: 'Taksi' }
    ]
  },
  {
    id: 'aircraft_parts',
    name: 'Uçak Parçaları',
    pairs: [
      { en: 'Wing', tr: 'Kanat' },
      { en: 'Tail', tr: 'Kuyruk' },
      { en: 'Engine', tr: 'Motor' },
      { en: 'Fuselage', tr: 'Gövde' },
      { en: 'Landing gear', tr: 'İniş Takımı' },
      { en: 'Propeller', tr: 'Pervane' },
      { en: 'Flap', tr: 'Flap' },
      { en: 'Rudder', tr: 'Dümen' },
      { en: 'Nose', tr: 'Burun' },
      { en: 'Door', tr: 'Kapı' }
    ]
  },
  {
    id: 'cabin_items',
    name: 'Kabin Eşyaları',
    pairs: [
      { en: 'Overhead bin', tr: 'Üst Bagaj' },
      { en: 'Tray table', tr: 'Tepsi Masası' },
      { en: 'Armrest', tr: 'Kol Dayama' },
      { en: 'Galley', tr: 'Mutfak' },
      { en: 'Lavatory', tr: 'Tuvalet' },
      { en: 'Trolley', tr: 'Servis Arabası' },
      { en: 'Reading light', tr: 'Okuma Lambası' },
      { en: 'Air vent', tr: 'Hava Menfezi' },
      { en: 'Call button', tr: 'Çağrı Düğmesi' },
      { en: 'Window shade', tr: 'Pencere Perdesi' }
    ]
  },
  {
    id: 'commands',
    name: 'Komutlar',
    pairs: [
      { en: 'Fasten', tr: 'Bağlayın' },
      { en: 'Stow', tr: 'Yerleştirin' },
      { en: 'Return', tr: 'Dönün' },
      { en: 'Remain seated', tr: 'Oturun' },
      { en: 'Follow me', tr: 'Beni takip edin' },
      { en: 'Please wait', tr: 'Lütfen bekleyin' },
      { en: 'Stand up', tr: 'Ayağa kalkın' },
      { en: 'Sit down', tr: 'Oturun' },
      { en: 'Move forward', tr: 'İlerleyin' },
      { en: 'Stop', tr: 'Durun' }
    ]
  },
  {
    id: 'polite_phrases',
    name: 'Kibar İfadeler',
    pairs: [
      { en: 'Certainly', tr: 'Tabii ki' },
      { en: 'My pleasure', tr: 'Memnuniyetle' },
      { en: 'Of course', tr: 'Elbette' },
      { en: 'Right away', tr: 'Hemen' },
      { en: 'Excuse me', tr: 'Pardon' },
      { en: 'I apologize', tr: 'Özür dilerim' },
      { en: 'May I help you', tr: 'Yardımcı olabilir miyim' },
      { en: 'You\'re welcome', tr: 'Rica ederim' },
      { en: 'Thank you', tr: 'Teşekkürler' },
      { en: 'Please', tr: 'Lütfen' }
    ]
  }
];

export default function CabinCrewDragDrop() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedSet, setSelectedSet] = useState(null);
  const [shuffledTurkish, setShuffledTurkish] = useState([]);
  const [matches, setMatches] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const initializeGame = (setId) => {
    const set = MATCHING_SETS.find(s => s.id === setId);
    if (set) {
      const shuffled = [...set.pairs.map(p => p.tr)].sort(() => Math.random() - 0.5);
      setShuffledTurkish(shuffled);
      setMatches({});
      setScore(0);
      setAttempts(0);
      setCompleted(false);
    }
  };

  const handleSetSelect = (setId) => {
    setSelectedSet(setId);
    initializeGame(setId);
  };

  const currentSet = MATCHING_SETS.find(s => s.id === selectedSet);

  const handleDragStart = (e, item, type) => {
    setDraggedItem({ item, type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem, targetType) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const { item: sourceItem, type: sourceType } = draggedItem;
    
    // Only allow dropping English on Turkish or vice versa
    if (sourceType === targetType) {
      setDraggedItem(null);
      return;
    }
    
    const englishWord = sourceType === 'en' ? sourceItem : targetItem;
    const turkishWord = sourceType === 'tr' ? sourceItem : targetItem;
    
    // Check if correct match
    const pair = currentSet.pairs.find(p => p.en === englishWord);
    const isCorrect = pair && pair.tr === turkishWord;
    
    setAttempts(prev => prev + 1);
    
    if (isCorrect) {
      setMatches(prev => ({ ...prev, [englishWord]: turkishWord }));
      setScore(prev => prev + 1);
      toast.success('Doğru eşleştirme! 🎉');
      
      // Check if all matched
      if (Object.keys(matches).length + 1 === currentSet.pairs.length) {
        setTimeout(() => setCompleted(true), 500);
      }
    } else {
      toast.error('Yanlış eşleştirme!');
    }
    
    setDraggedItem(null);
  };

  const isMatched = (word, type) => {
    if (type === 'en') {
      return matches[word] !== undefined;
    } else {
      return Object.values(matches).includes(word);
    }
  };

  const resetGame = () => {
    setSelectedSet(null);
    setMatches({});
    setScore(0);
    setAttempts(0);
    setCompleted(false);
  };

  // Set selection screen
  if (!selectedSet) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
          <div className="container mx-auto max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Ana Sayfa
              </Button>
              <div>
                <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
                  <Target className="w-8 h-8 text-green-500" />
                  Sürükle & Bırak Eşleştirme
                </h1>
                <p className="text-gray-600">İngilizce kelimeleri Türkçe karşılıklarına sürükle</p>
              </div>
            </div>

            {/* Set Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {MATCHING_SETS.map((set) => (
                <Card 
                  key={set.id}
                  className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-transparent hover:border-green-300"
                  onClick={() => handleSetSelect(set.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                        <Target className="w-7 h-7 text-white" />
                    </div>
                    <Badge className="bg-green-500 text-white">
                      {set.pairs.length} Çift
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{set.name}</h3>
                  <p className="text-sm text-gray-500">
                    {set.pairs.slice(0, 3).map(p => p.en).join(', ')}...
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      </CabinCrewLayout>
    );
  }

  // Completed screen
  if (completed) {
    const accuracy = Math.round((score / attempts) * 100);
    
    return (
      <CabinCrewLayout>
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 flex items-center justify-center">
          <Card className="max-w-md w-full border-4 border-green-300 shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl font-black text-gray-800 mb-4">
                Mükemmel! 🎉
              </h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-100 rounded-xl p-4">
                  <p className="text-3xl font-black text-green-600">{score}/{currentSet.pairs.length}</p>
                  <p className="text-sm text-gray-600">Doğru Eşleştirme</p>
                </div>
                <div className="bg-blue-100 rounded-xl p-4">
                  <p className="text-3xl font-black text-blue-600">%{accuracy}</p>
                  <p className="text-sm text-gray-600">Doğruluk</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={resetGame}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Farklı Set Seç
              </Button>
              <Button
                onClick={() => {
                  setMatches({});
                  setScore(0);
                  setAttempts(0);
                  setCompleted(false);
                  const shuffled = [...currentSet.pairs.map(p => p.tr)].sort(() => Math.random() - 0.5);
                  setShuffledTurkish(shuffled);
                }}
                variant="outline"
                className="w-full"
              >
                Aynı Seti Tekrar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      </CabinCrewLayout>
    );
  }

  // Game screen
  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={resetGame}
            >
              <ChevronLeft className="w-5 h-5" />
              Geri
            </Button>
            <div className="text-center">
              <p className="text-sm text-gray-500">{currentSet?.name}</p>
              <p className="font-bold text-gray-800">
                {Object.keys(matches).length} / {currentSet?.pairs.length} Eşleşti
              </p>
            </div>
            <Badge className="bg-green-500 text-white">
              {score} Puan
            </Badge>
          </div>

        {/* Progress bar */}
        <div className="h-3 bg-gray-200 rounded-full mb-8 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all"
            style={{ width: `${(Object.keys(matches).length / currentSet.pairs.length) * 100}%` }}
          />
        </div>

        {/* Game area */}
        <div className="grid grid-cols-2 gap-8">
          {/* English Column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-4 h-4 bg-sky-500 rounded-full" />
              <h3 className="font-bold text-gray-700">English</h3>
            </div>
            <div className="space-y-3">
              {currentSet?.pairs.map((pair, idx) => (
                <div
                  key={idx}
                  draggable={!isMatched(pair.en, 'en')}
                  onDragStart={(e) => handleDragStart(e, pair.en, 'en')}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, pair.en, 'en')}
                  className={`p-4 rounded-xl font-medium text-center transition-all ${
                    isMatched(pair.en, 'en')
                      ? 'bg-green-100 border-2 border-green-400 text-green-700'
                      : 'bg-sky-100 border-2 border-sky-300 text-gray-800 cursor-grab hover:shadow-lg hover:-translate-y-1 active:cursor-grabbing'
                  }`}
                >
                  {pair.en}
                  {isMatched(pair.en, 'en') && (
                    <Check className="w-5 h-5 inline-block ml-2 text-green-600" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Turkish Column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-4 h-4 bg-indigo-500 rounded-full" />
              <h3 className="font-bold text-gray-700">Türkçe</h3>
            </div>
            <div className="space-y-3">
              {shuffledTurkish.map((tr, idx) => (
                <div
                  key={idx}
                  draggable={!isMatched(tr, 'tr')}
                  onDragStart={(e) => handleDragStart(e, tr, 'tr')}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, tr, 'tr')}
                  className={`p-4 rounded-xl font-medium text-center transition-all ${
                    isMatched(tr, 'tr')
                      ? 'bg-green-100 border-2 border-green-400 text-green-700'
                      : 'bg-indigo-100 border-2 border-indigo-300 text-gray-800 cursor-grab hover:shadow-lg hover:-translate-y-1 active:cursor-grabbing'
                  }`}
                >
                  {tr}
                  {isMatched(tr, 'tr') && (
                    <Check className="w-5 h-5 inline-block ml-2 text-green-600" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>💡 İngilizce kelimeyi sürükleyip doğru Türkçe karşılığına bırakın</p>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
}
